import type { WidenImagePropsKey } from '@common/Widen/WidenImage';
import type { SortAlgorithm } from '@libs/opensearch/validation';
import type { MandatoryFooterProp } from '@models/api/page-content.types';

export const APP_NAME = 'catalog';

export const IGUS_LOCALE_COOKIE_NAME = 'igus_locale';

export const SESSION_KEEP_ALIVE_TIMEOUT = 1500000; // 25 min
export enum EnvironmentName {
  LOCAL = 'local',
  BLACK = 'black',
  WHITE = 'white',
  ORANGE = 'orange',
  AWS = 'aws',
}

export enum DeliveryTime {
  ON_REQUEST = '0',
  SLOW = '1',
  FAST = '2',
  FASTLANE = '3',
}

export enum SortType {
  PRICE_ASC = 'price_asc',
  PRICE_DESC = 'price_desc',
  DELIVERY_TIMES = 'delivery_times',
  BENDING_RADIUS_DESC = 'bending_radius_desc',
  NEW = 'new',
  MANUAL = 'manual',
}

export enum MetricType {
  MM = 'mm',
  INCH = 'inch',
}

export enum MeasurementSystem {
  Metric = 'Metric',
  Imperial = 'Imperial',
  Default = 'Metric',
}

export const DEFAULT_SORT_TYPE = SortType.MANUAL;
export const DEFAULT_SORT_ALGORITHM: SortAlgorithm = 'manual';

export const DEFAULT_METRIC_TYPE = MetricType.MM;

// how many products per page are shown on the category overview
export const PRODUCTS_PER_PAGE = 20;
export const IBH_PRODUCTS_PER_PAGE = 30;

export const DEFAULT_CURRENCY = 'EUR';
export const DEFAULT_PRICE_COUNTRY = 'DE';
export const DEFAULT_LOCALE = 'de-DE';

// igus.eu is the fallback domain
export const FALLBACK_DOMAIN = 'https://www.igus.eu';

export const LANG_EN_GB = 'en-GB';
export const LANG_DE_DE = 'de-DE';
export const LANG_FR_FR = 'fr-FR';
export const LANG_EN_EU = 'en';
export const LANG_DE = 'de';
export const DEFAULT_ZIP_CODE_DE = '51147';
export const DEFAULT_DISTRICT_CODE_FR = '632';
export const TRANSLATION_CONFIGURATION = {
  locales: [LANG_DE_DE, LANG_EN_GB],
  defaultLocale: LANG_DE_DE,
  // fallbackLocale shall be used when values for the current user locale are not available
  fallbackLocale: LANG_EN_EU,
  // extraFallbackLocale is another safetynet for when data for the fallbackLocale is not available as well
  extraFallbackLocale: LANG_DE_DE,
};

export const getLocale = (localeBase: { locale?: string }) =>
  localeBase?.locale ?? TRANSLATION_CONFIGURATION.fallbackLocale;

export const QUERY_KEYS = {
  ARTICLE_DETAIL_NUMBER_MODE: 'ARTICLE_DETAIL_NUMBER_MODE',
  ARTICLE_DETAIL_TEXT_MODE: 'ARTICLE_DETAIL_TEXT_MODE',
  ARTICLE_CONFIG: 'ARTICLE_CONFIG',
  CURRENT_PRODUCT: 'CURRENT_PRODUCT',
  ARTICLE_DETAIL: 'ARTICLE_DETAIL',
  ASSOCIATION_PRODUCTS: 'ASSOCIATION_PRODUCTS',
  PRODUCT_DOWNLOAD_ASSETS: 'PRODUCT_DOWNLOAD_ASSETS',
  PRODUCT_TECHNICAL_DATA: 'PRODUCT_TECHNICAL_DATA',
  PRODUCT_TECHNICAL_DRAWINGS_DATA: 'PRODUCT_TECHNICAL_DRAWINGS_DATA',
  SHELVES_MODELS_ASSOCIATIONS: 'SHELVES_MODELS_ASSOCIATIONS',
  ONLINE_TOOLS_TEASER: 'ONLINE_TOOLS_TEASER',
  CATEGORY_CONFIG: 'CATEGORY_CONFIG',
  CATEGORY_DATA: 'CATEGORY_DATA',
  CATEGORY_PAGE_CONTENT: 'CATEGORY_PAGE_CONTENT',
  FILTER_CARDINALITY: 'FILTER_CARDINALITY',
};

export const MMA_SERVICE_API_CALL_DEBOUNCE_DELAY = 300;

export const META_TAGS = {
  WEB_TRENDS: {
    'WT.cg_n': {
      // Key = categoryL1 || webTrendCGNKey
      // Question: Why are we maintaining this manually?
      chainflex: 'Chainflex',
      drygear: 'drygear',
      'e-motors': 'E-Motors',
      'e-chains': 'E-Chains',
      readycable: 'readycable',
      hoses: 'Hoses',
      prt: 'PRT',
      gears: 'Gears',
      xiros: 'xiros',
      iglidur: 'iglidur',
      igubal: 'igubal',
      dryspin: 'drylin SD',
      tribotape: 'Tribo Tape',
      drylin: 'Drylin',
      roller: 'Roller',
      '3d-printing-material': '3D Print material',
      triflex: 'triflex',
      'bar-stock': 'Bar Stock',
    },
    'WT.cg_s': 'Catalog | Overview', // hardcoded for category pages
  },
};

export const PRODUCT_OVERVIEW_VIEW_TYPE_STORAGE_KEY = 'igus_list_view_type';
export const PRODUCT_OVERVIEW_METRIC_TYPE_STORAGE_KEY = 'igus_list_metric_type';
export const PRODUCT_OVERVIEW_MEASUREMENT_SYSTEM_STORAGE_KEY = 'igus_list_measurement_system'; // used in opensearch category.
export const PRODUCT_OVERVIEW_UNIT_SYSTEM_STORAGE_KEY = 'igus_list_unit_system';

export const ROUTES = {
  PRODUCT: {
    DETAILS: (modelKey: string, productKey: string) => `/product/${modelKey}?artnr=${productKey}`,
  },
};

export const DEFAULT_TEASER_IMAGE_URL = `/_${APP_NAME}/images/default.svg`;
export const WIDEN_CDN_IMAGE_BASE_URL = 'https://igus.widen.net/content/';
export const WIDEN_CDN_VIDEO_BASE_URL = 'https://igus.widen.net/content/';
export const WIDEN_CDN_GIF_BASE_URL = 'https://igus.widen.net/content/';

export enum WidenCropKeepArea {
  CENTER = 'c',
  NORTH = 'n',
  NORTH_EAST = 'ne',
  EAST = 'e',
  SOUTH = 's',
  SOUTH_EAST = 'se',
  SOUTH_WEST = 'sw',
  WEST = 'w',
  NORTH_WEST = 'nw',
}

// Convert the list of allowed props to array because typescript has no
// native functionality to convert types union to set of required types
export const OBLIGATORY_FOOTER_FIELDS: MandatoryFooterProp[] = [
  'feedback',
  'about',
  'services',
  'contact',
  'payment',
  'language',
  'country',
  'boilerplate',
];

// Parameters that are used for the Widen URL query
export const WIDEN_QUERY_PROPS: Array<WidenImagePropsKey> = [
  'width',
  'height',
  'color',
  'crop',
  'keep',
  'quality',
  'animate',
];

export const WIDEN_DEFAULT_IMAGE_TYPE = 'webp';
export const WIDEN_DEFAULT_IMAGE_ID = 'XYZ';
export const WIDEN_DEFAULT_IMAGE_NAME = 'igus-image.webp';
export const WIDEN_DEFAULT_IMAGE_QUALITY = 65;

export const WIDEN_IMAGE_FILE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'bmp', 'tif', 'tiff', 'ai', 'svg', 'ico'];
export const WIDEN_GIF_FILE_EXTENSION = 'gif';
export const WIDEN_VIDEO_FILE_EXTENSION = 'mp4';

export const WIDEN_DEFAULT_ONLINE_TOOLS_TEASERS_IMAGE_WIDTH = 125;
export const WIDEN_DEFAULT_ONLINE_TOOLS_TEASERS_IMAGE_HEIGHT = 125;
export const WIDEN_ECHAIN_EXPERT_TEASER_IMAGE_WIDTH: Record<string, number> = {
  default: 212,
  lg: 160,
};
export const WIDEN_ECHAIN_EXPERT_TEASER_IMAGE_HEIGHT = 130;

// these values are here for reference but serve no purpose as the size is overwritten by the srcsets on the img tags generated by next js
export const WIDEN_DEFAULT_ONLINE_TEASERS_IMAGE_WIDTH = 150;
export const WIDEN_DEFAULT_ONLINE_TEASERS_IMAGE_HEIGHT = 150;
export const WIDEN_DEFAULT_OPENGRAPH_IMAGE_WIDTH = 1200;
export const WIDEN_DEFAULT_OPENGRAPH_IMAGE_HEIGHT = 630;

export const WIDEN_MAX_IMAGE_MEASUREMENT = 2048;

export const DRYLIN_T_MIN_BUY_QUANTITY = 1;
export const DRYLIN_T_MAX_BUY_QUANTITY = 9999;

export const TRIBOTAPE_MIN_BUY_QUANTITY = 1;
export const TRIBOTAPE_MAX_BUY_QUANTITY = 9999;
export const TRIBOTAPE_MIN_BUY_LENGTH = 500;
export const TRIBOTAPE_MAX_BUY_LENGTH = 50000;

export const CHAINFLEX_MIN_BUY_QUANTITY = 1;
export const CHAINFLEX_MAX_BUY_QUANTITY = 9999;
export const CHAINFLEX_MIN_BUY_LENGTH = 0.01;
export const CHAINFLEX_MAX_BUY_LENGTH = 10000;

export const LENGTH_WITH_CUTTING_COST_WITHOUT_COPPER_MIN_BUY_LENGTH = 1;
export const LENGTH_WITH_CUTTING_COST_WITHOUT_COPPER_MAX_BUY_LENGTH = 100000;

export const LENGTH_AND_PIECE_WITHOUT_COPPER_MIN_BUY_QUANTITY = 1;
export const LENGTH_AND_PIECE_WITHOUT_COPPER_MAX_BUY_QUANTITY = 9999;
export const LENGTH_AND_PIECE_WITHOUT_COPPER_MIN_BUY_LENGTH = 45;
export const LENGTH_AND_PIECE_WITHOUT_COPPER_MAX_BUY_LENGTH = 1500;

export const E_CHAIN_MIN_BUY_QUANTITY = 1;
export const E_CHAIN_MAX_BUY_QUANTITY = 9999;
export const E_CHAIN_MIN_BUY_LENGTH = 0.01;
export const E_CHAIN_MAX_BUY_LENGTH = 10000;

export const LEAD_SCREW_MIN_BUY_QUANTITY = 1;
export const LEAD_SCREW_MAX_BUY_QUANTITY = 100;
export const LEAD_SCREW_MIN_BUY_LENGTH = 1;
export const LEAD_SCREW_MAX_BUY_LENGTH = 10000;

/**
 * Category NodeId Overwrites
 *
 * The NodeIds for Categories should be read from the products in the Category to display MetaInformation, PageContents and the Contact Person
 * In some cases this does not work correctly, because products can be in multiple categories or on
 * general overview Pages like "Unkonfektionierte Leitungen" where products from all categories are displayed.
 * For those Categories the NodeId can be hardcoded here.
 * GetStaticProps of the Category Pages will first look here and then as a fallback look into the products to find the correct NodeId.
 * */

export const CATEGORY_NODEID_OVERWRITES: Record<string, number> = {
  Accessories: 301258,
  CF: 300732,
  Cables_for_robots: 301282,
  accessories_slewing_rings: 300810,
  ball_bearings: 300821,
  bevel_gears: 301519,
  bevel_gears_1_1: 301520,
  bevel_gears_1_1_5: 301521,
  bevel_gears_1_2: 301522,
  bevel_gears_1_3: 301523,
  bevel_gears_1_4: 301524,
  bevel_gears_1_5: 301525,
  bus_cable: 300736,
  cables_for_motion: 300731,
  connector_set: 301471,
  control_cables: 300733,
  coupling: 300896,
  data_cable: 300734,
  direct_current_motors_dc_motors: 300892,
  drive_cables_with_manufacturer_standard: 300745,
  drylin_lead_screw_technology: 300839,
  drylin_lead_screw_nuts: 300840,
  drylin_lead_screws: 301378,
  drylin_accessories: 301274,
  ec_bldc_motors: 301337,
  external_brake_resistors: 301447,
  fibre_optic_cable: 300737,
  gear_racks: 301510,
  gear_technology: 301925,
  gears_gear_racks: 301508,
  guide_rollers: 301975,
  harnessed_cables: 300741,
  initiators: 301336,
  joints_with_worm_gear: 301288,
  linear_actuators: 301527,
  measuring_system_cables: 300738,
  modular_gearbox_system: 301940,
  motor_accessories: 301332,
  motor_cables: 300740,
  motor_control_systems: 301331,
  motor_flanges: 300895,
  motor_gearboxes: 300897,
  motors: 300890,
  other_designs_ball_bearings: 300925,
  robot_cables: 301271,
  rollers: 301148,
  r_shaft_guides: 301213,
  self_aligning_bearings: 300727,
  sensor_and_actuator_cables: 300744,
  slewing_rings: 300809,
  slewing_rings_and_slewing_ring_bearings: 300808,
  slewing_rings_with_drive_for_robot_joints: 301288,
  special_cables: 301156,
  spur_gears: 301509,
  stepper_motors: 300893,
  suitable_for_Berger_Lahr: 300750,
  suitable_for_Jetter: 300757,
  suitable_for_NUM: 300761,
  toothed_slewing_rings: 301147,
  wave_gear: 301454,
  worm_gear_joints: 301946,
  '3D_printing_materials': 300820,
  '3D_movement': 300912,
  Network_Ethernet_FOC_fieldbus_cables: 300743,
  bar_stocks: 300811,
  round_rods: 300816,
  hollow_bars: 301275,
  plates: 300818,
  plate_stripes: 301385,
  sheets: 301963,
  strain_reliefs: 302087,
  smart_iot_solutions: 301467,
  low_cost_automation: 300845,
  video_vision_bus_systems: 300742,
  cables_with_industrial_connectors: 301257,
  connectors: 300861,
  intercontec_te: 301301,
  ftc: 301320,
  yamaichi: 301321,
  harting: 301322,
  hummel: 301325,
  binder: 301458,
  module_connect: 301640,
  tools_and_accessories: 301326,
  coatings: 301425,
  PMA_protective_hoses_and_accessories: 300851,
};

export const CATEGORY_ENTRYPOINT_KEY = 'Product_structure';

export const CATEGORY_ACCESSORIES_E_CHAINS_KEY = 'Accessories_e_chains';

export enum ShopTopPathSegments {
  CHAINFLEX = 'chainflex',
  READYCABLE = 'readycable',
  PRT = 'prt',
  E_CHAINS = 'e-chains',
}

// config for Regular prices min length based on category
export const MIN_LENGTH_REGULAR_PRICES: Record<string, number> = {
  pneumatic_hoses: 500,
  default: 1,
};

// TODO: this list includes all known asset keys and should be scoped accordingly
export const ASSOCIATED_PRODUCTS_DEFAULT_ASSETS = [
  'asset_drawing_images_setup_image',
  'attr_download_01',
  'attr_download_02',
  'attr_download_03',
  'attr_download_04',
  'attr_download_05',
  'attr_download_06',
  'attr_download_07',
  'attr_download_08',
  'attr_download_09',
  'attr_download_10',
  'attr_download_11',
  'attr_download_12',
  'attr_drawing_03',
  'attr_image_01',
  'attr_image_02',
  'attr_image_03',
  'attr_image_04',
  'attr_image_05',
  'attr_image_06',
  'attr_image_07',
  'attr_image_08',
  'attr_image_09',
  'attr_image_10',
  'attr_video_01',
  'attr_video_02',
  'attr_video_03',
  'diagram_asset_01',
  'diagram_asset_02',
  'diagram_asset_03',
  'diagram_asset_04',
  'diagram_asset_05',
  'diagram_asset_06',
  'diagram_asset_07',
  'diagram_asset_09',
  'diagram_asset_10',
  'diagram_asset_12',
  'drawing_01',
  'drawing_02',
  'drawing_04',
  'drawing_linear_module',
  'FLG_FLG_chart_01',
  'FLG_FLG_chart_01_text',
  'installation_dimension_drawing_02',
  'installation_dimension_drawing_03',
  'product_teaser_image',
];

export const BUS_CABLE_VARIANT_AXIS_ATTRIBUTES_ORDER = [
  'attr_bus_type_simple_select',
  'attr_number_of_cores_and_conductor_nominal_cross_section_simple_select',
];

export const RC_VARIANT_AXIS_ATTRIBUTES_ORDER = [
  'Manufacturer_Part_No',
  'cable_type',
  'attr_outer_diameter_max_metric_mm',
];
export const GEARS_VARIANT_AXIS_ATTRIBUTES_ORDER = [
  'modul_simple_select',
  'capital_L', // only used in category gear_racks =  Zahnstangen
  'attr_capital_b', // only used in category gear_racks =  Zahnstangen
  'attr_h1', // only used in category gear_racks =  Zahnstangen
  'number_of_teeth_simple_select',
  'd1_bore_diameter_metric',
];

export const ECHAIN_VARIANT_AXIS_ATTRIBUTES_ORDER = ['inner_width', 'bend_radius'];

export const FLANGE_BALL_BEARINGS_UNIT_VARIANT_AXIS_ATTRIBUTES_ORDER = [
  'diameter_d1',
  'attr_outer_diameter_d2',
  'installation_size',
];

export const BALL_BEARINGS_VARIANT_AXIS_ATTRIBUTES_ORDER = [
  'diameter_d1',
  'attr_outer_diameter_d2',
  'attr_bearing_length_b1',
  'installation_size',
];

export const CONVEYOR_ROLLERS_VARIANT_AXIS_ATTRIBUTES_ORDER = [
  'diameter_d1',
  'attr_outer_diameter_d2',
  'installation_size',
];

export const BALL_TRANSFER_UNIT_VARIANT_AXIS_ATTRIBUTES_ORDER = [
  'diameter_d1',
  'attr_trackball_diameter',
  'max_axial_static_strength',
];

export const TOOTHED_SLEWING_RINGS = ['prt_tooth_type_simple_select', 'lowercase_d'];

export const SPHERICAL_BALLS = ['attr_shaft_diameter', 'attr_capital_b'];

export const IGUBAL_DOUBLE_JOINTS_ORDER = [
  'attr_shaft_diameter',
  'diameter_d1',
  'alignment_version',
  'Material_tube',
  'attr_capital_x',
  'attr_outer_diameter_d2',
];

export const IGUBAL_COUPLING_JOINTS_ORDER = [
  'attr_thread_d1_simple_select',
  'alignment_version',
  'Material_tube',
  'Material_ball_stud',
];

export const DRYGEAR_ACCESSORIES_ORDER = ['motor_type_simple_select', 'attr_design_simple_select'];

export const DRYGEAR_WORM_GEAR_JOINTS_ASYMMETRICAL_ORDER = ['transmission_simple_select', 'motor_pin_metric'];

export const DRYGEAR_WORM_GEAR_ACCESSORIES_ORDER = [
  'installation_size',
  'motor_type_simple_select',
  'attr_design_simple_select',
  'diameter_capital_s',
];

export const MOTOR_GEAR_BOXES_ORDER = ['drive_shaft_diameter', 'ratio_i_simple_select'];

// Prices for domains of below countries do not depend on session.priceCountry.
// The user will see the prices that belong to the locale of the domain
// We only show the DeliveryLocationBanner for these countries
// We do not dynamically reload prices for these countries when domainLocale != session.priceCountry
export const localesThatUseIPForPrices = ['es', 'en', 'fr'];

export const AVAILABLE_PAYMENT_METHODS = (locale = TRANSLATION_CONFIGURATION.fallbackLocale) => {
  switch (locale) {
    case 'de-AT':
      return ['INVOICE'];
    default:
      return ['INVOICE', 'PREPAYMENT', 'CREDIT_CARD', 'PAYPAL'];
  }
};

/**
 * The MANUFACTURER_MMA_ID is used in the rc search of to MMA
 * */
export const MANUFACTURER_MMA_ID: Record<string, number> = {
  suitable_for_Allen_Bradley: 19,
  suitable_for_B_R: 1,
  suitable_for_Baumueller: 2,
  suitable_for_Beckhoff: 3,
  suitable_for_Bosch_Rexroth: 13,
  suitable_for_Control_Techniques: 5,
  suitable_for_Siemens: 15,
  suitable_for_Danaher_Motion: 6,
  suitable_for_ELAU: 7,
  suitable_for_FAGOR: 8,
  suitable_for_FANUC: 9,
  suitable_for_Festo: 23,
  suitable_for_Heidenhain: 10,
  suitable_for_Jetter: 20,
  suitable_for_Berger_Lahr: 4,
  suitable_for_Lenze: 11,
  suitable_for_LinMot: 24,
  suitable_for_LTi_DRiVES: 18,
  suitable_for_Mitsubishi: 21,
  suitable_for_NUM: 12,
  suitable_for_Omron: 17,
  suitable_for_Parker: 22,
  suitable_for_SEW: 14,
  suitable_for_Stoeber: 16,
  // Delta, Harmonic Drive, Hengstler, KEB, Panasonic, Wittenstein don't have IDs yet
};

export const isBrowsingInternationalShop = () =>
  typeof window?.location !== 'undefined' && typeof window?.location.hostname !== 'undefined'
    ? window.location.hostname.indexOf('igus.eu') !== -1
    : false;

export const PRIMARY_CATEGORY: Record<string, string> = {
  ECHAIN: 'linear_movement',
  TOOTHED_SLEWING_RINGS: 'toothed_slewing_rings',
  SPHERICAL_BALLS: 'spherical_balls',
  BAR_STOCKS: 'bar_stocks',
};

export const countryCurrencies = {
  AE: 'AED',
  AR: 'USD',
  AT: 'EUR',
  AU: 'AUD',
  BA: 'BAM',
  BE: 'EUR',
  BG: 'BGN',
  BH: 'EUR',
  BN: 'SGD',
  BR: 'BRL',
  BY: 'EUR',
  CA: 'USD',
  CH: 'CHF',
  CL: 'EUR',
  CN: 'RMB',
  CO: 'COP',
  CR: 'USD',
  CY: 'EUR',
  CZ: 'CZK',
  DE: 'EUR',
  DK: 'EUR',
  EC: 'USD',
  EE: 'EUR',
  EG: 'EGP',
  IE: 'EUR',
  IL: 'EUR',
  ES: 'EUR',
  FI: 'EUR',
  FR: 'EUR',
  GR: 'EUR',
  HR: 'HRK',
  HU: 'EUR',
  ID: 'IDR',
  IN: 'INR',
  IQ: 'EUR',
  IR: 'EUR',
  IT: 'EUR',
  JP: 'JPY',
  KR: 'WON',
  KW: 'EUR',
  LB: 'EUR',
  LT: 'EUR',
  LU: 'EUR',
  LV: 'EUR',
  MK: 'EUR',
  MX: 'USD',
  MY: 'MYR',
  NL: 'EUR',
  NO: 'NOK',
  NZ: 'NZD',
  OM: 'AED',
  PA: 'USD',
  PE: 'USD',
  PH: 'SGD',
  PK: 'EUR',
  PL: 'PLN',
  PT: 'EUR',
  QA: 'EUR',
  RO: 'EUR',
  RS: 'RSD',
  RU: 'EUR',
  SA: 'EUR',
  SE: 'SEK',
  SG: 'SGD',
  SI: 'EUR',
  SK: 'EUR',
  TH: 'THB',
  TR: 'EUR',
  TW: 'TWD',
  UA: 'EUR',
  UK: 'GBP',
  US: 'USD',
  UY: 'USD',
  VE: 'EUR',
  VN: 'VND',
  ZA: 'ZAR',
  TN: 'EUR',
};

/*
 * In order to reduce the page size of the product detail page the fetched product data is reduced and unnecessary
 * product attributes are removed. This list contains the names of the necessary attributes that should not be removed
 * from the data.
 * */
export const REQUIRED_PRODUCT_PAGE_ATTRIBUTES = [
  'CFCase_S_length_max_metric',
  'CFCase_S_length_min_metric',
  'CFCase_length_max_metric',
  'CFCase_length_min_metric',
  'K',
  'K2',
  'WT_cg_n',
  'WT_cg_s',
  'add_to_echain_configuration',
  'application_example',
  'attr_USP',
  'attr_certificates_Icons',
  'attr_copper_index_metric_kg_km',
  'attr_description',
  'attr_ipro_ProdID',
  'attr_number_of_cores_and_conductor_nominal_cross_section_simple_select',
  'attr_outer_diameter_max_metric_mm',
  'attr_status_new_product_boolean',
  'attr_status_phase_out_model_boolean',
  'attr_teaser',
  'attr_gliding_bool',
  'bend_radius',
  'cad_data_bool',
  'cf_case_size',
  'cf_lifetime_table',
  'e_plan',
  'frontend_top_level_attributes',
  'igubal_expert_release_bool',
  'hi',
  'max_cable_diameter_metric',
  'opening_principle',
  'inner_width',
  'mounted_bool',
  'new_variant',
  'node_contact_ID',
  'online_tools',
  'only_with_e_skin_bool',
  'order_type',
  'pitch_e_chain',
  'product_ID_CMS',
  'product_notes',
  'release_of_roler_configurator_bool',
  'release_of_service_life_calculation_ball_bearing_bool',
  'sample_ordering_allowed_bool',
  'series_description',
  'shipping_weight',
  'sorting_mounting_bracket_group',
  'standard_separator',
  'standard_shelf',
  'webshop_technical_data_attributes',
  'when_not_to_use',
  'when_to_use',
  'zuken_release_bool',
  'travel_distance_supported_min_metric',
  'travel_distance_supported_max_metric',
  'attr_meta_title',
  'attr_meta_description',
  'standard_bool',
  'release_drylin_leadscrew_configurator',
  'release_of_service_life_calculation_ESL_bool',
  'release_of_service_life_calculation_CSL_bool',
  'release_cable_for_readycable_product_finder_bool',
  'uneven_number_of_links',
  'partnumber_imperial', // id_us is derived from this akeneo attribute
  'partnumber_imperial_old',
];

export const REQUIRED_ASSOCIATED_PRODUCT_ATTRIBUTES = [
  'allignment_of_mounting_bracket', // don't change: there is a typo in the Akeneo
  'article_description_text_area',
  'attr_description',
  'attr_design_simple_select',
  'attr_USP',
  'attr_outer_diameter_max_metric_mm',
  'attr_single_usp',
  'attr_status_new_product_boolean',
  'attr_status_phase_out_model_boolean',
  'cf_case_size',
  'complete_kit_even_bool',
  'complete_kit_uneven_bool',
  'description',
  'lenght', // don't change: there is a typo in the Akeneo
  'length_mounting_bracket_or_set',
  'mounted_bool',
  'mounting_bracket_type_2',
  'mounting_variant',
  'mounting_variant_icon',
  'new_variant',
  'online_tools',
  'order_type',
  'part_number',
  'series_description',
  'set_article',
  'sorting',
  'standard_bool',
  'type_support_tray',
  'variant_of_mounting_bracket',
  'wall_bracket',
  'wall_bracket_text',
  'width',
];

export const TECHNICAL_DATA_DIAGRAM_PRODUCT_ASSETS_ATTRIBUTES = [
  'diagram_asset_underline_01',
  'diagram_asset_underline_02',
  'diagram_asset_underline_03',
  'diagram_asset_underline_04',
  'diagram_asset_underline_05',
  'diagram_asset_underline_06',
  'diagram_asset_underline_07',
  'diagram_asset_underline_08',
  'diagram_asset_underline_09',
  'diagram_asset_underline_10',
  'diagram_asset_underline_11',
  'diagram_asset_underline_12',
];

export const JSONLD_PRODUCT_ATTRIBUTES = [
  'attr_status_phase_out_model_boolean',
  'series_description',
  'shipping_weight',
] as const;

export const BLACKLISTED_MODELS_FOR_CATEGORY_PAGE = ['MAT0012678', 'MAT0012614'];
export const ROUTE_MAPPING_PAGINATION_LIMIT = 10000;

export const CATEGORIES_EXCLUDED_FROM_TREE = ['cables_for_motion.accessory'];

/**
 * Ensures that the paths of these categories are removed so that they do not become part of the HAproxy ACLs.
 */
export const BLACKLISTED_CATEGORIES = [
  'cables_for_motion.accessory',
  'e_chains.Accessories_e_chains',
  'Accessories_e_chains.interior_separators',
  'interior_separators.separators',
  'interior_separators.horizontal_shelves',
  'Accessories_e_chains.mounting_brackets',
];

/**
 * Categories, whose children are partially enabled in our shop
 */
export const WHITELISTED_PARENT_CATEGORIES = [
  'drive_technology_for_linear_movements',
  'drylin_linear_technology',
  'e_chains',
  'Accessories_e_chains',
];

export const E_CHAIN_EXPERT_TEASER_ID = 'e_chain_expert';
export const INCH_COMPATIBLE_CATEGORIES = [
  // self_aligning_bearings
  'Product_structure.self_aligning_bearings',
  'self_aligning_bearings.rod_ends',
  'self_aligning_bearings.spherical_balls',
  'self_aligning_bearings.spherical_insert_bearings',
  'self_aligning_bearings.variable_double_joint',
  'self_aligning_bearings.double_joints',
  'self_aligning_bearings.igubal_clevis_joint_combinations',
  'self_aligning_bearings.igubal_flange_bearings',
  'self_aligning_bearings.igubal_spherical_bearings',
  'self_aligning_bearings.igubal_pillow_block_bearings',
  'self_aligning_bearings.igubal_angled_in_line_ball_and_socket_joints',
  'self_aligning_bearings.igubal_self_aligning_clip_bearings',
  'self_aligning_bearings.igubal_clevis_joint_accessory',
  'self_aligning_bearings.other_designs',

  // ball_bearings
  'Product_structure.ball_bearings',
  'ball_bearings.radial_ball_bearings',

  // drylin_lead_screw_technology
  'Product_structure.drylin_lead_screw_technology',
  'drylin_lead_screw_technology.drylin_lead_screw_nuts',
  'drylin_lead_screw_technology.drylin_lead_screws',
  'drylin_lead_screw_nuts.acme_nuts',
  'drylin_lead_screws.drylin_acme_lead_screws',

  // r_shaft_guides
  'Product_structure.drylin_linear_technology.r_shaft_guides',
  'r_shaft_guides.r_shafts',
  'r_shaft_guides.r_pillow_blocks',
  'r_shaft_guides.r_carriages',
  'r_shaft_guides.r_linear_slide_bearings',
  'r_shaft_guides.r_liner',
  'r_shaft_guides.gliding_pads',
  'r_shaft_guides.r_shaft_end_supports_shaft_end_blocks',
];

// Child or parent categories where unit system switch is hidden
export const HIDE_UNIT_SWITCH_CATEGORIES = [
  'drylin_lead_screw_nuts.acme_nuts',
  'drylin_lead_screws.drylin_acme_lead_screws',
];

export const PRODUCT_MEASUREMENT_SYSTEM_URL_QUERY_PARAM = 'metric';

export enum IbhFilterKeys {
  MATERIAL = 'material',
  PRODUCTION_METHOD = 'productionMethod',
  TEMPERATURE_RANGE_MIN = 'temperatureRangeMin',
  TEMPERATURE_RANGE_MAX = 'temperatureRangeMax',
  FDA_COMPLIANCE = 'fdaCompliance',
  EU102011_COMPLIANCE = 'eu102011Compliance',
  DIRT_AND_DUST = 'dirtAndDust',
  LOW_WATER_ABSORBTION = 'lowWaterAbsorption',
  CHEMICALS = 'chemicals',
  UNDERWATER_SPLASHWATER = 'underwaterSplashwater',
  ELECTRICALLY_CONDUCTIVE = 'electricallyConductive',
  MAX_SURFACE_PRESSURE = 'maxSurfacePressure',
  DIMENSIONS = 'dimensions',
  QUANTITY = 'quantity',
  PTFE_FREE = 'PTFEFree',
  PFAS_FREE = 'PFASFree',
}

export const MAIN_IBH_CATEGORY_KEY = 'iglidur-ibh';
export const MAIN_IGLIDE_IBH_CATEGORY_KEY = 'iglide-ibh';

export type MAIN_IBH_INFO_PAGE_PATH_LOCALES = 'de-DE' | 'de-BE' | 'en-US' | 'en-CA' | 'es-MX' | 'fr-CA' | 'default';
export const MAIN_IBH_INFO_PAGE_PATH: {
  [key in MAIN_IBH_INFO_PAGE_PATH_LOCALES]: string;
} = {
  'de-DE': '/info/gleitlager-onlineshop',
  'de-BE': '/info/gleitlager-onlineshop',
  'en-US': '/plastic-bearings',
  'en-CA': '/info/plain-plastic-bearings',
  'fr-CA': '/info/plain-bearings-plain-bearings-iglidur',
  'es-MX': '/info/plain-bearings-plain-bearings-iglidur',
  default: '/info/plain-bearings-online-shop',
};

export const COUNTRIES_INCLUDED_COPPER_PRICE_IN_PRICE_PER_METER = ['UK', 'IN', 'IE', 'BR', 'US', 'CA', 'MX'];

export const IMPERIAL_UNIT_SYSTEM_LOCALES = ['en-US', 'en-CA', 'es-MX', 'fr-CA'];
export const CATEGORIES_FOR_UNIT_SYSTEM_METRIC = [
  'drylin_linear_technology',
  'w_profileguides',
  'w_rails',
  'n_low_profile_guides',
  'n_rails',
  't_rail_guide',
  't_rails',
  'r_shaft_guides',
  'r_shafts',
  'q_linear_square_guide',
  'q_section_rails',
  'drylin_lead_screw_technology',
  'drylin_lead_screws',
  'drylin_high_helix_lead_screws_dryspin',
  'drylin_lead_screw_spindles_dryspin',
  'drylin_trapezoidal_lead_screws',
  'drylin_metric_lead_screws',
  'liner',
  'bar_stocks',
  'round_rods',
  'hollow_bars',
  'plate_stripes',
  'sheets',
];

// For these locales we display/use the US-specific article numbers
export const LOCALES_TO_USE_US_PRODUCT_ID = ['en-US', 'en-CA', 'es-MX', 'fr-CA'];

export const LOCALE_PATHS_FROM_MULTILANGUAGE_DOMAINS = ['it-CH', 'fr-CH', 'fr-BE', 'de-BE', 'fr-CA'];

export const VAT_FACTORS = {
  'de-DE': 1.19,
};

export const VAT_CATEGORIES = [
  'ball_bearings',
  'slewing_rings_and_slewing_ring_bearings',
  't_rail_guide',
  'drylin_lead_screw_technology',
];

export const MOUNTING_BRACKET_FAMILY_CODES = [
  'mounting_bracket_kma',
  'mounting_bracket_plastic',
  'mounting_bracket_steel',
  'more_products',
];

export const RELEASED_ORDER_TRACKING_LOCALES = [
  'de-DE',
  'de-AT',
  'nl-BE',
  'fr-BE',
  'de-CH',
  'fr-CH',
  'it-CH',
  'en',
  'es',
  'da-DK',
  'en-EE',
  'es-ES',
  'fr-FR',
  'fr',
  'hu-HU',
  'it-IT',
  'nl-NL',
  'pl-PL',
  'pt-PT',
];

/**
 * This constant defines how the prices and their currency for a giving locale should be formatted.
 * This is especially used for locales where:
 * - the currency has to be shown as a smybol instead of the code
 * - the number has to be formatted in a special way (Refer to Intl.NumberFormat documentation)
 */
export const PRICE_FORMATTER_OPTIONS: Record<string, Intl.NumberFormatOptions> = {
  default: {
    style: 'currency',
    currencyDisplay: 'code',
  },
  'en-GB': {
    style: 'currency',
    currencyDisplay: 'symbol',
  },
  'pt-BR': {
    style: 'currency',
    currencyDisplay: 'symbol',
  },
  'zh-TW': {
    style: 'currency',
    currencyDisplay: 'code',
    maximumFractionDigits: 0,
  },
};

// TODO : ONLINE_TOOLS_TEASER_US_IDS Should be removed within COM-8364
export const ONLINE_TOOLS_TEASER_US_SPECIFIC_IDS = [
  'individual_leitungs_konfigurator',
  'lebensdauerrechnerfuerleitungen',
  'produktfinderfuerleitungen',
  'quickrobot',
  'e_chain_expert',
  'lead_screw_drive_expert',
  'lead_screw_configurator',
];
